/* eslint-disable react-hooks/set-state-in-effect */
import { logger } from '@/lib/logger';
import { useEffect, useRef, useState } from 'react';
import { usePrivyRegistration, usePrivyRestart, usePrivyStatus } from '@/hooks/usePrivy';
import { CheckCircle, AlertCircle, Loader2, RefreshCw } from 'lucide-react';

interface KYCVerificationProps {
  onComplete?: () => void;
  onError?: (error: string) => void;
}

type VerificationStatus = 'idle' | 'loading' | 'verifying' | 'in_review' | 'otp_limit' | 'restarting' | 'success' | 'error';
type CameraPermission = 'unknown' | 'requesting' | 'granted' | 'denied';

// Privy iframe sends postMessage events — detect OTP limit exhaustion
// Known error codes from Privy: otp_limit, max_otp, OTP_LIMIT_EXCEEDED, etc.
const OTP_LIMIT_PATTERNS = [
  'otp_limit',
  'otp limit',
  'max_otp',
  'OTP_LIMIT',
  'otp_exceeded',
  'too many otp',
  'batas otp',
  'limit otp',
];

function isOtpLimitMessage(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const msg = data as Record<string, unknown>;
  const searchable = JSON.stringify(msg).toLowerCase();
  return OTP_LIMIT_PATTERNS.some(p => searchable.includes(p.toLowerCase()));
}

export const KYCVerification = ({ onComplete, onError }: KYCVerificationProps) => {
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>('idle');
  const [registrationUrl, setRegistrationUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [iframeBlocked, setIframeBlocked] = useState(false);
  const [cameraPermission, setCameraPermission] = useState<CameraPermission>('unknown');
  const hasStarted = useRef(false);
  const iframeOriginRef = useRef<string | null>(null);

  const registration = usePrivyRegistration();
  const restart = usePrivyRestart();
  const { data: statusData } = usePrivyStatus(
    verificationStatus === 'verifying' || verificationStatus === 'in_review'
  );

  // Extract and store the iframe origin once we have a URL
  useEffect(() => {
    if (registrationUrl) {
      try {
        iframeOriginRef.current = new URL(registrationUrl).origin;
      } catch {
        iframeOriginRef.current = null;
      }
    }
  }, [registrationUrl]);

  // Listen for postMessage from Privy iframe to detect OTP limit
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only trust messages from the Privy iframe origin (or any if origin unknown)
      if (iframeOriginRef.current && event.origin !== iframeOriginRef.current) return;
      if (verificationStatus !== 'verifying') return;

      if (isOtpLimitMessage(event.data)) {
        logger.warn('[KYC] OTP limit detected from Privy iframe postMessage', event.data);
        setVerificationStatus('otp_limit');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [verificationStatus]);

  // Start registration on mount
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const startRegistration = async () => {
      setVerificationStatus('loading');
      try {
        const data = await registration.mutateAsync();
        if (data.registrationUrl) {
          setRegistrationUrl(data.registrationUrl);
          setVerificationStatus('verifying');
        } else if (data.status === 'verified') {
          setVerificationStatus('success');
          setTimeout(() => {
            onComplete?.();
          }, 2000);
        } else if (data.status === 'rejected') {
          setVerificationStatus('error');
          setErrorMessage('Verification was rejected. Please try again.');
          onError?.('Verification was rejected.');
        } else if (
          data.status === 'in_review' ||
          data.status === 'submitted' ||
          data.status === 'pending_review'
        ) {
          // Already submitted, waiting for review — poll for final result
          setVerificationStatus('in_review');
        } else {
          // Still pending — start polling
          setVerificationStatus('verifying');
        }
      } catch (err) {
        logger.error('Failed to start registration:', err);
        setErrorMessage(err instanceof Error ? err.message : 'Failed to start verification');
        setVerificationStatus('error');
        onError?.('Failed to start verification. Please try again.');
      }
    };

    startRegistration();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Monitor status polling results
  useEffect(() => {
    if (!statusData) return;

    if (statusData.status === 'verified') {
      setVerificationStatus('success');
      setTimeout(() => {
        onComplete?.();
      }, 2000);
    } else if (statusData.status === 'rejected') {
      setVerificationStatus('error');
      setErrorMessage(statusData.reason || 'Verification was rejected.');
      onError?.('Verification was rejected.');
    } else if (
      statusData.status === 'in_review' ||
      statusData.status === 'submitted' ||
      statusData.status === 'pending_review'
    ) {
      // Callback received but review not yet complete — show dedicated screen
      setVerificationStatus('in_review');
    }
  }, [statusData, onComplete, onError]);

  const requestCameraAccess = async () => {
    setCameraPermission('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      stream.getTracks().forEach(track => track.stop());
      setCameraPermission('granted');
    } catch (err) {
      logger.warn('[KYC] Camera permission denied:', err);
      setCameraPermission('denied');
    }
  };

  const handleRestart = async () => {
    setVerificationStatus('restarting');
    setRegistrationUrl(null);
    setErrorMessage(null);
    iframeOriginRef.current = null;
    try {
      const data = await restart.mutateAsync();
      if (data.registrationUrl) {
        setRegistrationUrl(data.registrationUrl);
        setVerificationStatus('verifying');
      } else if (data.status === 'verified') {
        setVerificationStatus('success');
        setTimeout(() => onComplete?.(), 2000);
      } else {
        setVerificationStatus('verifying');
      }
    } catch (err) {
      logger.error('[KYC] Failed to restart registration:', err);
      setErrorMessage(err instanceof Error ? err.message : 'Failed to restart verification');
      setVerificationStatus('error');
      onError?.('Failed to restart verification. Please try again.');
    }
  };

  if (verificationStatus === 'loading') {
    return (
      <div className='flex flex-col items-center justify-center py-12 space-y-4'>
        <Loader2 className='w-12 h-12 text-blue-600 animate-spin' />
        <p className='text-sm text-gray-600'>Loading verification...</p>
      </div>
    );
  }

  if (verificationStatus === 'restarting') {
    return (
      <div className='flex flex-col items-center justify-center py-12 space-y-4'>
        <Loader2 className='w-12 h-12 text-blue-600 animate-spin' />
        <p className='text-sm text-gray-600'>Starting new verification session...</p>
      </div>
    );
  }

  if (verificationStatus === 'in_review') {
    return (
      <div className='p-8'>
        <div className='bg-blue-50 border border-blue-200 rounded-2xl p-6'>
          <div className='flex flex-col items-center text-center gap-4'>
            <div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center'>
              <Loader2 className='w-8 h-8 text-blue-600 animate-spin' />
            </div>
            <div>
              <p className='font-bold text-lg text-blue-900'>Verification Under Review</p>
              <p className='text-sm text-blue-700 mt-2 leading-relaxed'>
                Your documents have been submitted and are currently being reviewed by our team.
                This usually takes 24–48 hours.
              </p>
              <p className='text-xs text-blue-500 mt-3 font-medium'>
                You'll receive a notification once your verification is approved.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'otp_limit') {
    return (
      <div className='p-6'>
        <div className='bg-amber-50 border border-amber-200 rounded-xl p-6'>
          <div className='flex flex-col items-center text-center gap-4'>
            <div className='w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center'>
              <RefreshCw className='w-7 h-7 text-amber-600' />
            </div>
            <div>
              <p className='font-semibold text-amber-900'>OTP Attempt Limit Reached</p>
              <p className='text-sm text-amber-700 mt-2 leading-relaxed'>
                You've reached the maximum number of OTP attempts for this session. We need to start
                a new verification session with a fresh code.
              </p>
            </div>
            <button
              onClick={handleRestart}
              disabled={restart.isPending}
              className='mt-2 px-6 py-3 bg-amber-600 text-white text-sm font-semibold rounded-xl hover:bg-amber-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2'
            >
              {restart.isPending ? (
                <>
                  <Loader2 className='w-4 h-4 animate-spin' />
                  Starting new session...
                </>
              ) : (
                <>
                  <RefreshCw className='w-4 h-4' />
                  Start New Verification
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'error') {
    return (
      <div className='p-6 bg-red-50 border border-red-200 rounded-xl'>
        <div className='flex items-start gap-3'>
          <AlertCircle className='w-6 h-6 text-red-600 flex-shrink-0 mt-0.5' />
          <div>
            <p className='font-semibold text-red-900'>Verification Error</p>
            <p className='text-sm text-red-700 mt-1'>
              {errorMessage || 'Failed to load verification system'}
            </p>
            <button
              onClick={async () => {
                setErrorMessage(null);
                setRegistrationUrl(null);
                setVerificationStatus('loading');
                try {
                  const data = await registration.mutateAsync();
                  if (data.registrationUrl) {
                    setRegistrationUrl(data.registrationUrl);
                    setVerificationStatus('verifying');
                  } else if (data.status === 'verified') {
                    setVerificationStatus('success');
                    setTimeout(() => onComplete?.(), 2000);
                  } else if (data.status === 'rejected') {
                    setVerificationStatus('error');
                    setErrorMessage('Verification was rejected. Please try again.');
                  } else {
                    setVerificationStatus('verifying');
                  }
                } catch (err) {
                  logger.error('Failed to start registration:', err);
                  setErrorMessage(
                    err instanceof Error ? err.message : 'Failed to start verification'
                  );
                  setVerificationStatus('error');
                }
              }}
              className='mt-3 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors'
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'success') {
    return (
      <div className='p-8'>
        <div className='bg-green-50 border border-green-200 rounded-2xl p-6'>
          <div className='flex flex-col items-center text-center gap-4'>
            <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center'>
              <CheckCircle className='w-8 h-8 text-green-600' />
            </div>
            <div>
              <p className='font-bold text-lg text-green-900'>Documents Submitted!</p>
              <p className='text-sm text-green-700 mt-2 leading-relaxed'>
                Your identity verification has been submitted successfully. Our team will review
                your documents within 24-48 hours.
              </p>
              <p className='text-xs text-green-600 mt-3 font-medium'>
                You'll receive a notification once your verification is approved.
              </p>
            </div>
            <button
              onClick={() => onComplete?.()}
              className='mt-4 px-6 py-3 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-lg'
            >
              Continue to Activate Prime Access
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='w-full py-6 md:py-8'>
      <div className='mb-6 text-center'>
        <h3 className='text-lg font-black text-gray-900 tracking-tight'>
          Identity Verification Required
        </h3>
        <p className='text-xs text-gray-500 mt-2 font-medium'>
          Complete KYC verification to access ASET PRIME
        </p>
      </div>

      {registrationUrl ? (
        <div className='w-full mx-auto'>
          {cameraPermission !== 'granted' ? (
            <div className='flex flex-col items-center justify-center py-10 gap-5 border border-gray-100 rounded-2xl bg-gray-50 px-6 text-center'>
              <div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center'>
                <svg className='w-8 h-8 text-blue-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 10l4.553-2.069A1 1 0 0121 8.82v6.36a1 1 0 01-1.447.89L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z' />
                </svg>
              </div>
              <div>
                <p className='font-semibold text-gray-900'>Camera Access Required</p>
                <p className='text-sm text-gray-500 mt-2 leading-relaxed'>
                  {cameraPermission === 'denied'
                    ? 'Camera access was denied. Please allow camera access in your browser settings, or open verification in a new tab.'
                    : 'To complete liveness verification, we need access to your camera. Click the button below to grant access.'}
                </p>
              </div>
              {cameraPermission === 'denied' ? (
                <button
                  type='button'
                  onClick={() => window.open(registrationUrl, '_blank', 'noopener,noreferrer')}
                  className='px-6 py-3 text-sm font-semibold rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors'
                >
                  Open Verification in New Tab
                </button>
              ) : (
                <button
                  type='button'
                  onClick={requestCameraAccess}
                  disabled={cameraPermission === 'requesting'}
                  className='px-6 py-3 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-60 flex items-center gap-2'
                >
                  {cameraPermission === 'requesting' ? (
                    <>
                      <Loader2 className='w-4 h-4 animate-spin' />
                      Requesting access...
                    </>
                  ) : (
                    'Allow Camera Access'
                  )}
                </button>
              )}
              <button
                type='button'
                onClick={() => window.open(registrationUrl, '_blank', 'noopener,noreferrer')}
                className='text-xs text-blue-600 underline underline-offset-2 hover:text-blue-700'
              >
                Skip and open in new tab instead
              </button>
            </div>
          ) : (
            <>
              <div className='mb-4 flex flex-col items-center gap-3 text-center'>
                <p className='text-xs text-gray-500'>
                  Complete your identity verification below. If the camera doesn't work, use the button
                  to open verification in a new tab.
                </p>
                <div className='flex items-center gap-2 flex-wrap justify-center'>
                  <button
                    type='button'
                    onClick={() => window.open(registrationUrl, '_blank', 'noopener,noreferrer')}
                    className='px-4 py-2 text-xs font-semibold rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors'
                  >
                    Open Verification in New Tab
                  </button>
                  <button
                    type='button'
                    onClick={handleRestart}
                    disabled={restart.isPending}
                    className='px-4 py-2 text-xs font-semibold rounded-lg border border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-60 flex items-center gap-1'
                  >
                    <RefreshCw className='w-3 h-3' />
                    Restart Session
                  </button>
                </div>
              </div>
              {iframeBlocked ? (
                <div className='flex flex-col items-center justify-center py-16 gap-4 border border-gray-100 rounded-2xl bg-gray-50'>
                  <p className='text-sm text-gray-600 text-center px-4'>
                    Verification cannot be displayed here. Please open it in a new tab to continue.
                  </p>
                  <button
                    type='button'
                    onClick={() => window.open(registrationUrl, '_blank', 'noopener,noreferrer')}
                    className='px-6 py-3 text-sm font-semibold rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors'
                  >
                    Open Verification in New Tab
                  </button>
                </div>
              ) : (
                <div className='relative border border-gray-100 rounded-2xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.04)] bg-white min-h-[500px]'>
                  <iframe
                    src={registrationUrl}
                    title='KYC Verification'
                    className='w-full min-h-[500px] border-0'
                    allow='camera *; microphone *; fullscreen *'
                    allowFullScreen
                    referrerPolicy='no-referrer-when-downgrade'
                    style={{ height: '600px' }}
                    onError={() => setIframeBlocked(true)}
                  />
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className='flex flex-col items-center justify-center py-12 space-y-4'>
          <Loader2 className='w-8 h-8 text-blue-600 animate-spin' />
          <p className='text-sm text-gray-600'>Your verification is being reviewed...</p>
          <p className='text-xs text-gray-400'>This may take a few moments</p>
        </div>
      )}
    </div>
  );
};
